import React, { useState, useEffect } from 'react';
import { getCollaborators } from '../services/api';
import { CollaboratorEntity, SplitCollaboratorInput } from '../types';

interface CollaboratorSelectorProps {
  value: SplitCollaboratorInput;
  onChange: (collaborator: SplitCollaboratorInput) => void;
  onRemove: () => void;
  index: number;
  canRemove: boolean;
  disabled?: boolean;
  selectedCollaboratorIds?: string[]; // New prop to filter out selected collaborators
  accountId?: string; // Account to filter collaborators by
}

const CollaboratorSelector: React.FC<CollaboratorSelectorProps> = ({
  value,
  onChange,
  onRemove,
  index,
  canRemove,
  disabled = false,
  selectedCollaboratorIds = [],
  accountId
}) => {
  const [availableCollaborators, setAvailableCollaborators] = useState<CollaboratorEntity[]>([]);
  const [loadingCollaborators, setLoadingCollaborators] = useState(false);
  const [mode, setMode] = useState<'existing' | 'new'>('existing');

  useEffect(() => {
    loadAvailableCollaborators();
  }, [accountId]);

  const loadAvailableCollaborators = async () => {
    try {
      setLoadingCollaborators(true);
      const collaborators = await getCollaborators(accountId);
      setAvailableCollaborators(collaborators);
    } catch (error) {
      console.error('Failed to load collaborators:', error);
    } finally {
      setLoadingCollaborators(false);
    }
  };

  const handleModeChange = (newMode: 'existing' | 'new') => {
    setMode(newMode);
    
    if (newMode === 'existing') {
      // Clear manual entry fields but keep existing data if available
      onChange({
        collaboratorId: undefined, // Use undefined instead of empty string
        role: value.role,
        share: value.share,
        name: value.name || '',
        email: value.email || '',
        ipiNumber: value.ipiNumber || '',
        pro: value.pro || ''
      });
    } else {
      // Clear collaborator ID
      onChange({
        collaboratorId: undefined,
        role: value.role,
        share: value.share,
        name: value.name || '',
        email: value.email || '',
        ipiNumber: value.ipiNumber || '',
        pro: value.pro || ''
      });
    }
  };

  const handleCollaboratorSelect = (collaboratorId: string) => {
    if (!collaboratorId) {
      // Handle deselection - clear collaborator data but keep role and share
      onChange({
        collaboratorId: undefined,
        role: value.role,
        share: value.share,
        name: '',
        email: '',
        ipiNumber: '',
        pro: ''
      });
      return;
    }

    const selectedCollaborator = availableFilteredCollaborators.find(c => c.id === collaboratorId);
    if (selectedCollaborator) {
      onChange({
        collaboratorId: selectedCollaborator.id,
        role: value.role,
        share: value.share,
        name: selectedCollaborator.name,
        email: selectedCollaborator.email,
        ipiNumber: selectedCollaborator.ipiNumber,
        pro: selectedCollaborator.pro
      });
    }
  };

  const handleFieldChange = (field: keyof SplitCollaboratorInput, fieldValue: string | number | boolean) => {
    onChange({
      ...value,
      [field]: fieldValue
    });
  };

  // Filter out already selected collaborators, but keep the current selection if it exists
  const availableFilteredCollaborators = availableCollaborators.filter(collaborator => {
    // Always include the currently selected collaborator for this selector
    if (value.collaboratorId === collaborator.id) {
      return true;
    }
    // Exclude collaborators that are already selected in other selectors
    return !selectedCollaboratorIds.includes(collaborator.id);
  });

  return (
    <div className="bg-surface-secondary border border-border-subtle rounded-lg p-4 mb-3">
      {/* Header row with number badge and remove button */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 bg-accent rounded-full flex items-center justify-center text-xs font-medium text-content">
            {index + 1}
          </span>
          <span className="text-sm font-medium text-content">Collaborator {index + 1}</span>
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="text-content-muted hover:text-state-error transition-colors disabled:opacity-50"
            title="Remove collaborator"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Mode Selection - more compact toggle style */}
      {availableFilteredCollaborators.length > 0 && (
        <div className="flex mb-3">
          <button
            type="button"
            onClick={() => handleModeChange('existing')}
            disabled={disabled}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-l-lg border transition-colors disabled:opacity-50 ${
              mode === 'existing'
                ? 'bg-accent text-content border-accent'
                : 'bg-surface-tertiary text-content border-border-subtle'
            }`}
          >
            Select Existing
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('new')}
            disabled={disabled}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-r-lg border-y border-r transition-colors disabled:opacity-50 ${
              mode === 'new'
                ? 'bg-accent text-content border-accent'
                : 'bg-surface-tertiary text-content border-border-subtle'
            }`}
          >
            Add New
          </button>
        </div>
      )}

      {/* Collaborator Selection/Input - no labels, just placeholders */}
      {mode === 'existing' && availableFilteredCollaborators.length > 0 ? (
        <div className="space-y-3">
          {loadingCollaborators ? (
            <div className="w-full px-3 py-2.5 bg-surface-tertiary border border-border-subtle rounded-md text-content-muted text-sm">
              Loading collaborators...
            </div>
          ) : (
            <select
              value={value.collaboratorId || ''}
              onChange={(e) => handleCollaboratorSelect(e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2.5 bg-surface-tertiary border border-border-subtle rounded-md text-content focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 text-sm"
              required
            >
              <option value="">Choose a collaborator...</option>
              {availableFilteredCollaborators.length === 0 ? (
                <option value="" disabled>No available collaborators</option>
              ) : (
                availableFilteredCollaborators.map(collaborator => (
                  <option key={collaborator.id} value={collaborator.id}>
                    {collaborator.name} {collaborator.email && `(${collaborator.email})`}
                  </option>
                  ))
                )}
              </select>
            )}
          
          {/* Role and Share side by side for existing collaborators */}
          <div className="grid grid-cols-2 gap-3">
            <select
              value={value.role}
              onChange={(e) => handleFieldChange('role', e.target.value as 'composer' | 'author' | 'both')}
              disabled={disabled}
              className="w-full px-3 py-2.5 bg-surface-tertiary border border-border-subtle rounded-md text-content focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 text-sm"
            >
              <option value="composer">Composer</option>
              <option value="author">Author</option>
              <option value="both">Both</option>
            </select>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={value.share || ''}
              onChange={(e) => handleFieldChange('share', parseFloat(e.target.value) || 0)}
              onWheel={(e) => e.currentTarget.blur()}
              disabled={disabled}
              className="w-full px-3 py-2.5 bg-surface-tertiary border border-border-subtle rounded-md text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 text-sm"
              placeholder="Share %"
              required
            />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Name input */}
          <input
            type="text"
            value={value.name || ''}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2.5 bg-surface-tertiary border border-border-subtle rounded-md text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 text-sm"
            placeholder="Collaborator name"
            required
          />

          {/* Role and Share side by side */}
          <div className="grid grid-cols-2 gap-3">
            <select
              value={value.role}
              onChange={(e) => handleFieldChange('role', e.target.value as 'composer' | 'author' | 'both')}
              disabled={disabled}
              className="w-full px-3 py-2.5 bg-surface-tertiary border border-border-subtle rounded-md text-content focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 text-sm"
            >
              <option value="composer">Composer</option>
              <option value="author">Author</option>
              <option value="both">Both</option>
            </select>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={value.share || ''}
              onChange={(e) => handleFieldChange('share', parseFloat(e.target.value) || 0)}
              onWheel={(e) => e.currentTarget.blur()}
              disabled={disabled}
              className="w-full px-3 py-2.5 bg-surface-tertiary border border-border-subtle rounded-md text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 text-sm"
              placeholder="Share %"
              required
            />
          </div>

          {/* Email input */}
          <input
            type="email"
            value={value.email || ''}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2.5 bg-surface-tertiary border border-border-subtle rounded-md text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 text-sm"
            placeholder="Email (optional)"
          />
        </div>
      )}

      {/* Additional fields for new collaborators - collapsed by default */}
      {mode === 'new' && (
        <details className="mt-3">
          <summary className="text-xs text-accent cursor-pointer hover:text-accent-hover">
            + More details (IPI, PRO)
          </summary>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <input
              type="text"
              value={value.ipiNumber || ''}
              onChange={(e) => handleFieldChange('ipiNumber', e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2.5 bg-surface-tertiary border border-border-subtle rounded-md text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 text-sm"
              placeholder="IPI Number"
              pattern="[0-9]{9,11}"
            />
            <select
              value={value.pro || ''}
              onChange={(e) => handleFieldChange('pro', e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2.5 bg-surface-tertiary border border-border-subtle rounded-md text-content focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 text-sm"
            >
              <option value="">Select PRO...</option>
              <option value="ASCAP">ASCAP (USA)</option>
              <option value="BMI">BMI (USA)</option>
              <option value="SESAC">SESAC (USA)</option>
              <option value="GMR">GMR (USA)</option>
              <option value="PRS">PRS for Music (UK)</option>
              <option value="MCPS">MCPS (UK)</option>
              <option value="SOCAN">SOCAN (Canada)</option>
              <option value="APRA">APRA AMCOS (Australia)</option>
              <option value="GEMA">GEMA (Germany)</option>
              <option value="SACEM">SACEM (France)</option>
              <option value="SIAE">SIAE (Italy)</option>
              <option value="SGAE">SGAE (Spain)</option>
              <option value="JASRAC">JASRAC (Japan)</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Save as collaborator checkbox */}
          <label className="flex items-center gap-2 cursor-pointer mt-3">
            <input
              type="checkbox"
              checked={value.saveAsCollaborator || false}
              onChange={(e) => handleFieldChange('saveAsCollaborator', e.target.checked)}
              disabled={disabled}
              className="w-4 h-4 rounded border-border-subtle bg-surface-tertiary text-accent focus:ring-accent focus:ring-offset-0 disabled:opacity-50"
            />
            <span className="text-xs text-content-muted">
              Save for future splits
            </span>
          </label>
        </details>
      )}

      {/* Display selected collaborator details if in existing mode */}
      {mode === 'existing' && value.collaboratorId && (
        <div className="mt-3 p-3 bg-surface-tertiary/50 rounded-lg">
          {(() => {
            const selectedCollaborator = availableCollaborators.find(c => c.id === value.collaboratorId);
            if (!selectedCollaborator) return null;
            
            return (
              <div className="text-sm space-y-1">
                {selectedCollaborator.ipiNumber && (
                  <div className="flex items-center text-content-muted text-xs">
                    <span className="text-accent mr-2">IPI:</span>
                    {selectedCollaborator.ipiNumber}
                  </div>
                )}
                {selectedCollaborator.pro && (
                  <div className="flex items-center text-content-muted text-xs">
                    <span className="text-accent mr-2">PRO:</span>
                    {selectedCollaborator.pro}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default CollaboratorSelector;
